import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import type { ModelDescriptor } from "@racler/protocol";

/** Above this many models a plain <select> becomes unwieldy and the searchable picker takes over. */
export const PICKER_THRESHOLD = 15;

interface Props {
  models: ModelDescriptor[];
  value: string;
  disabled: boolean;
  onChange: (modelId: string) => void;
  emptyLabel?: string | undefined;
  providerName?: string | undefined;
}

/** Case-insensitive match on name or id (pure; unit-tested). */
export const filterModels = (models: ModelDescriptor[], query: string): ModelDescriptor[] => {
  const q = query.trim().toLowerCase();
  return q ? models.filter((m) => `${m.name} ${m.id}`.toLowerCase().includes(q)) : models;
};

/**
 * Model control of the composer. Few models: a native select. Many models: ONE compact button that opens a popover
 * with the search field and the list, so the Agent panel never spends permanent vertical space on model search.
 */
export function ModelSelect({ models, value, disabled, onChange, emptyLabel, providerName }: Props) {
  if (models.length <= PICKER_THRESHOLD) {
    return (
      <select
        aria-label="Model"
        data-testid="model-select"
        className="model-select"
        disabled={disabled || models.length === 0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {models.length === 0 && <option value="">{emptyLabel ?? "No models available"}</option>}
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}{providerName ? ` (${providerName})` : ""}
          </option>
        ))}
      </select>
    );
  }
  return (
    <ModelPicker
      models={models}
      value={value}
      disabled={disabled}
      onChange={onChange}
      providerName={providerName}
    />
  );
}

function ModelPicker({ models, value, disabled, onChange, providerName }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const shown = useMemo(() => filterModels(models, query), [models, query]);
  const current = models.find((m) => m.id === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="model-picker" ref={box}>
      <button
        type="button"
        className="model-picker-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Model"
        title={current?.id ?? "Choose a model"}
        disabled={disabled}
        onClick={() => (setOpen((v) => !v), setQuery(""))}
        data-testid="model-picker"
      >
        <span className="model-picker-label">{current?.name ?? "Choose model"}</span>
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && (
        <div className="model-popover" role="listbox" aria-label="Models" data-testid="model-popover">
          <label className="model-search">
            <Search size={13} aria-hidden />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search models…"
              aria-label="Search models"
              data-testid="model-search"
            />
          </label>
          <ul>
            {shown.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={m.id === value}
                  className={m.id === value ? "active" : ""}
                  onClick={() => (onChange(m.id), setOpen(false))}
                  data-testid={`model-option-${m.id}`}
                >
                  {m.name}
                  {providerName && <span className="muted small"> · {providerName}</span>}
                </button>
              </li>
            ))}
            {shown.length === 0 && <li className="muted small model-none">No matching models.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
